import './css/fonts.css'
import './css/global.css'
import './css/buttons.css'
import './css/navigation.css'
import './css/lang-toggler.css'
import './css/slider.css'
import { PublicationsOverview } from '@components/overview/publicationsOverview'
import { PublicationsList } from '@components/overview/publicationsList'
import { EventsOverview } from '@components/overview/eventsOverview'
import hugArrow from '@helpers/hug'

customElements.define('publications-overview', PublicationsOverview)
customElements.define('publications-list', PublicationsList)
customElements.define('events-overview', EventsOverview)

/* ---------- TOGGLE MENU ---------- */

const toggleMenu = document.querySelector('#burger')
const mainNav = document.querySelector('#main-navigation')
let scrollPosition = 0
let isOpen = false
let trapInertEls: Element[] = []

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

function getFocusable(): HTMLElement[] {
    return Array.from(mainNav.querySelectorAll<HTMLElement>(FOCUSABLE))
}

function trapFocus(e: KeyboardEvent): void {
    if (e.key === 'Escape') {
        toggleMenu.dispatchEvent(new MouseEvent('click'))
        ;(toggleMenu as HTMLElement).focus()
        return
    }
    if (e.key !== 'Tab') return
    const focusable = getFocusable()
    if (focusable.length === 0) return
    const first = focusable[0]
    const last = focusable[focusable.length - 1]
    if (e.shiftKey) {
        if (document.activeElement === first) {
            e.preventDefault()
            ;(toggleMenu as HTMLElement).focus()
        } else if (document.activeElement === toggleMenu) {
            e.preventDefault()
            last.focus()
        }
    } else {
        if (document.activeElement === last) {
            e.preventDefault()
            ;(toggleMenu as HTMLElement).focus()
        } else if (document.activeElement === toggleMenu) {
            e.preventDefault()
            first.focus()
        }
    }
}

function preventFocusEscape(e: FocusEvent): void {
    const target = e.target as Node
    if (!mainNav.contains(target) && target !== toggleMenu) {
        getFocusable()[0]?.focus()
    }
}

if (toggleMenu) {
    toggleMenu.addEventListener('click', () => {
        isOpen = toggleMenu.classList.toggle('is-open')
        mainNav.classList.toggle('is-open')

        if (mainNav.classList.contains('is-open')) {
            mainNav.setAttribute('aria-hidden', 'false')
            mainNav.removeAttribute('inert')
            mainNav.removeAttribute('tabindex')
            toggleMenu.setAttribute('aria-expanded', 'true')
            toggleMenu.setAttribute('aria-label', 'Navigation schliessen')
            trapInertEls = Array.from(document.body.children).filter(
                (el) => el !== mainNav && !el.contains(toggleMenu)
            )
            trapInertEls.forEach((el) => {
                el.setAttribute('inert', '')
                el.setAttribute('aria-hidden', 'true')
            })
            document.addEventListener('keydown', trapFocus)
            document.addEventListener('focusin', preventFocusEscape)
            getFocusable()[0]?.focus()
        } else {
            mainNav.setAttribute('aria-hidden', 'true')
            mainNav.setAttribute('inert', '')
            mainNav.setAttribute('tabindex', '-1')
            toggleMenu.setAttribute('aria-expanded', 'false')
            toggleMenu.setAttribute('aria-label', 'Navigation öffnen')
            trapInertEls.forEach((el) => {
                el.removeAttribute('inert')
                el.removeAttribute('aria-hidden')
            })
            trapInertEls = []
            document.removeEventListener('keydown', trapFocus)
            document.removeEventListener('focusin', preventFocusEscape)
        }

        const delay = isOpen ? 200 : 0

        setTimeout(() => {
            if (isOpen) {
                // Save scroll position
                scrollPosition = window.scrollY

                // Lock scroll
                document.body.style.position = 'fixed'
                document.body.style.top = `-${scrollPosition}px`
                document.body.style.left = '0'
                document.body.style.right = '0'
            } else {
                // Unlock scroll
                document.body.style.position = ''
                document.body.style.top = ''
                document.body.style.left = ''
                document.body.style.right = ''

                // Restore scroll position
                window.scrollTo(0, scrollPosition)
            }
        }, delay)
    })
}

/* ---------- BURGER + LOGO COLOR OVER IMAGES ---------- */

const burger = document.querySelector('#burger')
const logo = document.querySelector('#header-logo .logo')
const headerBar = document.querySelector('#header-logo')
const heroEl = document.querySelector('[data-hero]')

if (burger || logo) {
    // Images the burger/logo can overlap. Add `data-nav-light` to opt extra elements
    // or narrow this selector if some images are too light to need white.
    const watchedImages = Array.from(document.querySelectorAll<HTMLElement>('img, [data-nav-light], .bg-darkgrey'))

    const overlaps = (a: DOMRect, b: DOMRect): boolean => a.left < b.right && a.right > b.left && a.top < b.bottom && a.bottom > b.top

    const isOnImage = (el: Element): boolean => {
        const rect = el.getBoundingClientRect()
        return watchedImages.some((img) => overlaps(rect, img.getBoundingClientRect()))
    }

    const updateNavColors = (): void => {
        // The header is transparent while it sits over the hero and turns solid white
        // once the hero has scrolled past
        const overContent = heroEl ? heroEl.getBoundingClientRect().bottom <= 0 : true
        headerBar?.classList.toggle('is-solid', overContent)

        // On the white bar they're always dark; over the hero they adapt to what's
        // behind them (white over a dark image, dark over a light hero).
        if (burger) {
            // While the menu is open the burger is an X on a white panel — keep it dark.
            burger.classList.toggle('on-image', !overContent && !burger.classList.contains('is-open') && isOnImage(burger))
        }
        logo?.classList.toggle('on-image', !overContent && isOnImage(logo))
    }

    let ticking = false
    const requestUpdate = (): void => {
        if (ticking) return
        ticking = true
        requestAnimationFrame(() => {
            updateNavColors()
            ticking = false
        })
    }

    window.addEventListener('scroll', requestUpdate, { passive: true })
    window.addEventListener('resize', requestUpdate)
    updateNavColors()
}

const langToggler = document.querySelector('#lang-toggler')

if (langToggler) {
    console.log('test')
}

/* ---------- SCROLL-AWARE HEADER ---------- */

const burgerWrapper = document.querySelector<HTMLElement>('#burger-wrapper')
const headerLogo = document.querySelector<HTMLElement>('#header-logo')
// Only home page relevant
const homeLogo = document.querySelector<HTMLElement>('#header-logo .logo.text-logoHome')

if (burgerWrapper || headerLogo) {
    let lastScrollY = window.scrollY
    let headerTicking = false
    const HIDE_THRESHOLD = homeLogo ? 250 : 100
    const SHRINK_THRESHOLD = 60

    let logoSmall: boolean | null = null
    let logoAnim: Animation | null = null
    const setLogoSmall = (small: boolean, animate = true) => {
        if (!homeLogo || small === logoSmall) return
        logoSmall = small
        logoAnim?.cancel()
        if (!animate) {
            homeLogo.classList.toggle('is-small', small)
            return
        }
        const from = getComputedStyle(homeLogo).fontSize
        homeLogo.classList.toggle('is-small', small)
        const to = getComputedStyle(homeLogo).fontSize
        if (from === to) return
        logoAnim = homeLogo.animate(
            [{ fontSize: from }, { fontSize: to }],
            { duration: 600, easing: 'cubic-bezier(0.3, 0, 0, 1)' },
        )
    }

    const updateScrollHeader = () => {
        const y = window.scrollY
        const scrollingDown = y > lastScrollY
        const pastTop = y > HIDE_THRESHOLD
        const menuOpen = (toggleMenu as HTMLElement)?.classList.contains('is-open') ?? false

        if (scrollingDown && pastTop && !menuOpen) {
            burgerWrapper?.classList.add('is-hidden')
            headerLogo?.classList.add('is-hidden')
        } else if (!scrollingDown || y <= HIDE_THRESHOLD) {
            burgerWrapper?.classList.remove('is-hidden')
            headerLogo?.classList.remove('is-hidden')
        }

        // Home: big logo at the top, regular size once scrolled into the hero.
        setLogoSmall(y > SHRINK_THRESHOLD)

        lastScrollY = y
        headerTicking = false
    }

    window.addEventListener(
        'scroll',
        () => {
            if (headerTicking) return
            headerTicking = true
            requestAnimationFrame(updateScrollHeader)
        },
        { passive: true },
    )

    setLogoSmall(window.scrollY > SHRINK_THRESHOLD, false)
}

/* external link in richtext */

document.querySelectorAll<HTMLAnchorElement>('.oi-richtext a[href], nav a[href], footer a[href]').forEach((link) => {
    try {
        const url = new URL(link.href, window.location.href)
        if (url.hostname !== window.location.hostname) {
            link.setAttribute('target', '_blank')
            link.setAttribute('rel', 'noopener noreferrer')
        }
    } catch {

    }
})

/* ---------- SLIDER ---------- */

const sliders = Array.from(document.querySelectorAll<HTMLElement>('[data-slider]'))

sliders.forEach((slider) => {
    const track = slider.querySelector<HTMLElement>('[data-slider-track]')
    if (!track) return

    const slides = Array.from(track.querySelectorAll<HTMLElement>('.slide'))
    if (slides.length < 2) return

    const prevButtons = Array.from(slider.querySelectorAll<HTMLButtonElement>('[data-slider-prev]'))
    const nextButtons = Array.from(slider.querySelectorAll<HTMLButtonElement>('[data-slider-next]'))
    const currentLabel = slider.querySelector<HTMLElement>('[data-slider-current]')
    const status = slider.querySelector<HTMLElement>('[data-slider-status]')

    let lastIndex = -1
    const pad = (n: number): string => String(n).padStart(2, '0')

    // The slide whose start is closest to the current scroll position.
    const activeIndex = (): number => {
        const scrollLeft = track.scrollLeft
        let closest = 0
        let smallest = Infinity
        slides.forEach((slide, i) => {
            const distance = Math.abs(slide.offsetLeft - scrollLeft)
            if (distance < smallest) {
                smallest = distance
                closest = i
            }
        })
        return closest
    }

    const goTo = (index: number): void => {
        const clamped = Math.max(0, Math.min(slides.length - 1, index))
        track.scrollTo({ left: slides[clamped].offsetLeft, behavior: 'smooth' })
    }

    const update = (): void => {
        const i = activeIndex()

        // Disable the arrows at the respective ends (no-op when unchanged).
        prevButtons.forEach((button) => (button.disabled = i === 0))
        nextButtons.forEach((button) => (button.disabled = i === slides.length - 1))

        if (i === lastIndex) return
        lastIndex = i

        // Mutate the DOM only when the slide actually changes — toggling `inert`
        // on every scroll frame triggers a scroll-anchoring feedback loop.
        slides.forEach((slide, idx) => slide.toggleAttribute('inert', idx !== i))
        if (currentLabel) currentLabel.textContent = pad(i + 1)
        if (status) status.textContent = `Folie ${i + 1} von ${slides.length}`
    }

    prevButtons.forEach((button) => button.addEventListener('click', () => goTo(activeIndex() - 1)))
    nextButtons.forEach((button) => button.addEventListener('click', () => goTo(activeIndex() + 1)))

    let ticking = false
    track.addEventListener(
        'scroll',
        () => {
            if (ticking) return
            ticking = true
            requestAnimationFrame(() => {
                update()
                ticking = false
            })
        },
        { passive: true },
    )

    window.addEventListener('resize', update)
    update()
})
// hug
hugArrow('.oi-tag', { root: document.body })
